import { Link } from 'react-router-dom';
import { ArrowUpRight, ChevronRight } from 'lucide-react';
import { useCurrentUser } from '@/auth/AuthProvider';
import { ORGANIZATION_LABELS, hasRole } from '@/auth/types';
import { displayYearLabel, useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { visibleNavGroups } from '@/navigation';
import { APP_NAME } from '@/components/PageHeader';
import { ChartBackdrop } from '@/layout/ChartBackdrop';
import { formatNumber, formatPercent } from '@/lib/format';
import { toWareki, todayISO, weekdayJa } from '@/lib/wareki';
import { cx } from '@/lib/cx';
import { PORTAL_WIDGETS, usePortalSummary } from './widgets';
import { useEffect } from 'react';

function Kpi({ label, value, unit, emphasis }: { label: string; value: string; unit?: string; emphasis?: boolean }) {
  return (
    <div className={cx('bs-kpi', emphasis && 'is-emphasis')}>
      <span className="bs-kpi__label">{label}</span>
      <span className="bs-kpi__value">
        <span className="bs-num">{value || '—'}</span>
        {value && unit && <small>{unit}</small>}
      </span>
    </div>
  );
}

/** ポータル（/years/:year） */
export function PortalPage() {
  const user = useCurrentUser();
  const { year, fiscalYear, yearPath } = useFiscalYear();
  const summary = usePortalSummary(year);
  const groups = visibleNavGroups(user).map((g) => ({ ...g, items: g.items.filter((i) => !i.hideOnPortal) }));
  const widgets = PORTAL_WIDGETS.filter((w) => hasRole(user, w.roles));
  const today = todayISO();

  useEffect(() => {
    document.title = `ポータル｜${APP_NAME}`;
  }, []);

  // L-34 消化率 = 計画 > 0 ? round(実績 / 計画 × 100) : 0
  const rate =
    summary == null ? null : summary.plannedDays ? Math.round(((summary.actualDays ?? 0) / summary.plannedDays) * 100) : 0;

  return (
    <div className="bs-portal">
      <section className="bs-hero">
        <ChartBackdrop density="sparse" labels={false} className="bs-hero__chart" />
        <div className="bs-hero__main">
          <p className="bs-hero__date">
            {toWareki(today)}（{weekdayJa(today)}）
          </p>
          <h1 className="bs-hero__title">
            <span className="bs-hero__year">{displayYearLabel(fiscalYear)}</span>
            <span className="bs-hero__year-sub bs-num">FY{year}</span>
          </h1>
          <p className="bs-hero__greeting">
            {user.organizationName || ORGANIZATION_LABELS[user.organizationType]}　{user.name} さん
          </p>
          <p className="bs-hero__period">
            事業期間　{toWareki(fiscalYear.startDate)} 〜 {toWareki(fiscalYear.endDate)}
          </p>
        </div>
        <div className="bs-hero__kpis" aria-label="監視日数の状況">
          <Kpi label="計画日数" value={formatNumber(summary?.plannedDays)} unit="日" />
          <Kpi label="実績日数" value={formatNumber(summary?.actualDays)} unit="日" />
          <Kpi label="消化率" value={rate == null ? '' : formatPercent(rate).replace('%', '')} unit="%" emphasis />
        </div>
      </section>

      {widgets.length > 0 && (
        <section className="bs-widgets" aria-label="本日の状況">
          {widgets.map((w, i) => {
            const Icon = w.icon;
            return (
              <article key={w.id} className={cx('bs-widget', `bs-widget--${w.span}`)} style={{ animationDelay: `${80 + i * 60}ms` }}>
                <header className="bs-widget__header">
                  <h2 className="bs-widget__title">
                    <Icon size={17} strokeWidth={1.8} aria-hidden />
                    {w.title}
                  </h2>
                  {w.link && (
                    <Link to={yearPath(w.link.path)} className="bs-widget__link">
                      {w.link.label}
                      <ChevronRight size={14} aria-hidden />
                    </Link>
                  )}
                </header>
                <div className="bs-widget__body">{w.render({ year, user, yearPath })}</div>
              </article>
            );
          })}
        </section>
      )}

      <section className="bs-menu" aria-labelledby="portal-menu-title">
        <div className="bs-menu__head">
          <h2 id="portal-menu-title" className="bs-menu__title">
            業務メニュー
          </h2>
          <span className="bs-menu__hint">各メニューは選択中の事業年度（{displayYearLabel(fiscalYear)}）のデータを表示します</span>
        </div>
        <div className="bs-menu__groups">
          {groups.map((g, gi) => (
            <section key={g.id} className="bs-menu-group" style={{ animationDelay: `${160 + gi * 50}ms` }}>
              <h3 className="bs-menu-group__title">
                <span className="bs-menu-group__code">{g.code}</span>
                {g.label}
              </h3>
              <ul className="bs-menu-group__list">
                {g.items.map((item, ii) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.id}>
                      <Link to={yearPath(item.path)} className={cx('bs-tile', item.status === 'planned' && 'is-planned')}>
                        <span className="bs-tile__icon" aria-hidden>
                          <Icon size={19} strokeWidth={1.7} />
                        </span>
                        <span className="bs-tile__text">
                          <span className="bs-tile__label">
                            {item.label}
                            {item.status === 'planned' && <span className="bs-tile__tag">準備中</span>}
                          </span>
                          {item.description && <span className="bs-tile__desc">{item.description}</span>}
                        </span>
                        <span className="bs-tile__code bs-num" aria-hidden>
                          {g.code}-{String(ii + 1).padStart(2, '0')}
                        </span>
                        <ArrowUpRight size={16} className="bs-tile__arrow" aria-hidden />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      </section>
    </div>
  );
}
